import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { backendClient } from '@/lib/backendClient';

export type UserRole = 'admin' | 'moderator' | 'practitioner' | 'partners' | 'user' | null;

export const useUserRole = () => {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      try {
        // Check if using backend or Supabase
        const useBackend = import.meta.env.VITE_USE_BACKEND === 'true';

        if (useBackend) {
          // Fetch from backend /auth/me endpoint which now includes role
          const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
          const response = await fetch(`${backendUrl}/auth/me`, {
            credentials: 'include' // Include cookies for authentication
          });

          if (response.ok) {
            const userData = await response.json();
            const userRole = userData.role as UserRole;
            setRole(userRole || 'user');
          } else {
            console.error('Error fetching user from backend');
            setRole('user');
          }
        } else {
          // Use Supabase RPC (legacy path)
          const { data, error } = await backendClient
            .rpc('get_user_role', { _user_id: user.id });

          if (error && error.code !== 'PGRST116') {
            console.error('Error fetching user role:', error);
            setRole('user');
          } else {
            const validRole = data as UserRole;
            setRole(validRole || 'user');
          }
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setRole('user');
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  const isAdmin = role === 'admin';
  const isModerator = role === 'moderator';
  const isPractitioner = role === 'practitioner';
  const isPartner = role === 'partners';
  const hasAdminAccess = isAdmin;
  const hasModeratorAccess = isAdmin || isModerator;
  const hasPractitionerAccess = isAdmin || isPractitioner;
  const hasPartnerAccess = isAdmin || isPartner;
  // All users now have group session access (with 1 group limit for individuals)
  const hasGroupSessionAccess = true;

  return {
    role,
    loading,
    isAdmin,
    isModerator,
    isPractitioner,
    isPartner,
    hasAdminAccess,
    hasModeratorAccess,
    hasPractitionerAccess,
    hasPartnerAccess,
    hasGroupSessionAccess,
  };
};
